/**
 * useGoogleCalendar — 내 Google 캘린더 연결 상태·일정 사본 구독 + 연결·해제·동기화·추가·수정·삭제.
 * 설계: docs/design/11_Google캘린더_연결설계.md
 *
 * - 화면을 열면(연결돼 있을 때) 한 번 sync 요청 → 아이폰·Google 에서 바꾼 것을 바로 받아 옴.
 *   서버가 1분 안의 반복 요청은 건너뛴다. 그 밖에는 서버 예약 동기화(15분).
 * - 어떤 캘린더를 가져올지는 서버 정본(state.imported, 2026-10-29) — setImported(ids) → op set_calendars → 재동기화.
 *   imported 가 없는 옛 연결은 전체 캘린더로 본다. 표시 켜기/끄기는 useUserCalendars(calendar_prefs)가 맡는다.
 * - rules 배포 전에는 구독이 permission-denied → mode "unavailable"(연결 버튼 숨김·안내만).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { googleCalendarRepo } from "@/services/googleCalendar.repository";
import { requestGoogleCalendarCode } from "@/services/googleIdentity";
import {
  importedCalendars,
  sortGoogleEvents,
  type GoogleEvent,
  type GoogleLinkState,
} from "@/domain/googleCalendar";
import type { PersonalEventInput } from "@/domain/personalEvent";

export type GoogleCalendarMode = "loading" | "unlinked" | "linked" | "needs_relink" | "unavailable" | "error";
export type GoogleCalendarBusy = "link" | "sync" | "unlink" | "set_calendars" | null;

const isDenied = (e: unknown) =>
  !!e && typeof e === "object" && (e as { code?: unknown }).code === "permission-denied";

/** 서버에 보낼 일정 입력 (ERP 일정 폼 값 그대로, 할 일 칸 제외) */
function toServerInput(input: PersonalEventInput) {
  return {
    title: input.title,
    date: input.date,
    all_day: input.all_day,
    ...(input.all_day ? {} : { start_time: input.start_time, end_time: input.end_time }),
    ...(input.memo ? { memo: input.memo } : {}),
  };
}

export function useGoogleCalendar() {
  const { user } = useAuth();
  const uid = user?.uid ?? "";
  const [state, setState] = useState<{ uid: string; value: GoogleLinkState | null; denied?: boolean; error?: string } | null>(null);
  const [events, setEvents] = useState<{ uid: string; list: GoogleEvent[] } | null>(null);
  const [busy, setBusy] = useState<GoogleCalendarBusy>(null);
  const syncedOnce = useRef("");

  useEffect(() => {
    if (!uid) return;
    let alive = true;
    const unsub = googleCalendarRepo.watchState(
      uid,
      (value) => { if (alive) setState({ uid, value }); },
      (e) => { if (alive) setState({ uid, value: null, denied: isDenied(e), error: e.message }); },
    );
    return () => { alive = false; unsub(); };
  }, [uid]);

  const cur = state && state.uid === uid ? state : null;
  const linkState = cur?.value ?? null;
  const mode: GoogleCalendarMode = !cur
    ? "loading"
    : cur.denied
      ? "unavailable"
      : cur.error
        ? "error"
        : !linkState
          ? "unlinked"
          : linkState.status;

  // 연결돼 있을 때만 사본 구독 (해제·미연결이면 비움)
  const watching = mode === "linked" || mode === "needs_relink";
  useEffect(() => {
    if (!uid || !watching) return;
    let alive = true;
    const unsub = googleCalendarRepo.watchEvents(
      uid,
      (list) => { if (alive) setEvents({ uid, list }); },
      () => { if (alive) setEvents({ uid, list: [] }); },
    );
    return () => { alive = false; unsub(); };
  }, [uid, watching]);

  // 화면 열 때 1회 동기화 (실패는 상태 문서 last_error 로 보임 — 여기선 조용히)
  useEffect(() => {
    if (mode !== "linked" || syncedOnce.current === uid) return;
    syncedOnce.current = uid;
    googleCalendarRepo.runOp(uid, "sync").catch(() => undefined);
  }, [mode, uid]);

  /** Google 계정의 캘린더 전체(가져오기 후보) */
  const calendars = useMemo(() => linkState?.calendars ?? [], [linkState]);
  /** 가져온 캘린더(화면에 보이는 것) */
  const imported = useMemo(() => importedCalendars(linkState), [linkState]);
  /** 아직 가져오기를 한 번도 안 골랐는지(옛 연결 → 온보딩 유도) */
  const needsPick = mode === "linked" && !linkState?.imported;

  const importedIds = useMemo(() => new Set(imported.map((c) => c.id)), [imported]);
  const allEvents = useMemo(
    () => (watching && events && events.uid === uid ? sortGoogleEvents(events.list).filter((e) => importedIds.has(e.calendar_id)) : []),
    [watching, events, uid, importedIds],
  );
  const calendarOf = useCallback((calId: string) => calendars.find((c) => c.id === calId), [calendars]);

  const run = useCallback(
    async <T,>(kind: Exclude<GoogleCalendarBusy, null> | null, fn: () => Promise<T>): Promise<T> => {
      if (kind) setBusy(kind);
      try {
        return await fn();
      } finally {
        if (kind) setBusy(null);
      }
    },
    [],
  );

  const connect = useCallback(
    () =>
      run("link", async () => {
        if (!uid) throw new Error("로그인이 필요합니다.");
        const code = await requestGoogleCalendarCode();
        syncedOnce.current = uid; // 연결 과정에서 이미 전체 동기화함
        return googleCalendarRepo.runOp<{ email: string }>(uid, "link", { code });
      }),
    [uid, run],
  );
  const disconnect = useCallback(() => run("unlink", () => googleCalendarRepo.runOp(uid, "unlink")), [uid, run]);
  const syncNow = useCallback(() => run("sync", () => googleCalendarRepo.runOp(uid, "sync", { force: true })), [uid, run]);
  /** 가져올 캘린더 목록 저장(서버가 재동기화하고 빠진 캘린더 사본을 지운다) */
  const setImported = useCallback(
    (ids: string[]) => run("set_calendars", () => googleCalendarRepo.runOp(uid, "set_calendars", { calendar_ids: ids })),
    [uid, run],
  );

  const create = useCallback(
    (calendarId: string, input: PersonalEventInput) =>
      googleCalendarRepo.runOp(uid, "create", { calendar_id: calendarId, input: toServerInput(input) }),
    [uid],
  );
  const update = useCallback(
    (ev: GoogleEvent, input: PersonalEventInput, memoChanged: boolean) =>
      googleCalendarRepo.runOp(uid, "update", { doc_id: ev.id, input: toServerInput(input), memo_changed: memoChanged }),
    [uid],
  );
  const remove = useCallback((ev: GoogleEvent) => googleCalendarRepo.runOp(uid, "delete", { doc_id: ev.id }), [uid]);

  return {
    mode,
    busy,
    state: linkState,
    calendars,
    imported,
    needsPick,
    events: allEvents,
    calendarOf,
    connect,
    disconnect,
    syncNow,
    setImported,
    create,
    update,
    remove,
  };
}

export type GoogleCalendarApi = ReturnType<typeof useGoogleCalendar>;
